import type React from "react";
import { Box, Paper, Typography, alpha } from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { useTranslation } from "react-i18next";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { DashboardKpiValue } from "../../types/dashboard";
import { formatDashboardDelta } from "../../utils/dashboardDelta";

type TranslationKeys = keyof typeof translations.en;

interface DashboardCountCardsProps {
  orders: DashboardKpiValue;
  standbyOrders: DashboardKpiValue;
  reports: DashboardKpiValue;
}

interface CountCardConfig {
  labelKey: TranslationKeys;
  value: DashboardKpiValue;
  icon: React.ReactNode;
  color: string;
  testId: string;
}

const DashboardCountCards: React.FC<DashboardCountCardsProps> = ({
  orders,
  standbyOrders,
  reports,
}) => {
  const { t } = useTranslation();

  const cards: CountCardConfig[] = [
    {
      labelKey: "dashboard.tripOrders",
      value: orders,
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
      color: "#3B82F6",
      testId: TestId.DASHBOARD.KPI_ORDERS,
    },
    {
      labelKey: "dashboard.standbyOrders",
      value: standbyOrders,
      icon: <EventBusyOutlinedIcon fontSize="small" />,
      color: "#8B5CF6",
      testId: TestId.DASHBOARD.KPI_STANDBY,
    },
    {
      labelKey: "dashboard.attentionReports",
      value: reports,
      icon: <AssignmentOutlinedIcon fontSize="small" />,
      color: "#0EA5E9",
      testId: TestId.DASHBOARD.KPI_REPORTS,
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
        mb: 3,
      }}
    >
      {cards.map((card) => (
        <Paper
          key={card.testId}
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(card.color, 0.12),
              color: card.color,
              flexShrink: 0,
            }}
          >
            {card.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t(card.labelKey)}
            </Typography>
            <Typography
              variant="h6"
              component="p"
              data-testid={card.testId}
              sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}
            >
              {card.value.current}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {formatDashboardDelta(card.value.current, card.value.previous, {
                showAbsoluteChange: true,
              })}
            </Typography>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default DashboardCountCards;
